import {
  ReasoningTrace,
  PhaseStatus,
  OutputType,
  ReasoningPhase,
  PhaseOutput,
  FileReference,
} from '@/services/reasoning-traces/types'

const generateMockFiles = (
  sessionId: string,
  count: number,
): FileReference[] => {
  const fileTypes = ['csv', 'json', 'png', 'pdf', 'txt', 'xlsx']
  const files: FileReference[] = []

  for (let i = 0; i < count; i++) {
    const fileType = fileTypes[Math.floor(Math.random() * fileTypes.length)]
    files.push({
      file_id: `file_${sessionId}_${i}`,
      filename: `analysis_output_${i + 1}.${fileType}`,
      path: `/workspace/${sessionId}/outputs/analysis_output_${
        i + 1
      }.${fileType}`,
      public_url: `/api/files/${sessionId}/analysis_output_${
        i + 1
      }.${fileType}`,
      file_type: fileType,
      size: Math.floor(Math.random() * 1000000) + 10000,
      description: `Generated ${fileType.toUpperCase()} file with analysis results`,
      created_at: new Date(Date.now() - Math.random() * 3600000).toISOString(),
    })
  }

  return files
}

const generateMockOutputs = (
  phaseId: string,
  type: 'backtesting' | 'quality' = 'backtesting',
): PhaseOutput[] => {
  const outputs: PhaseOutput[] = []

  if (type === 'backtesting') {
    // Add backtesting analysis text
    outputs.push({
      id: `${phaseId}_output_1`,
      output_type: OutputType.TEXT_MESSAGE,
      data: {
        message:
          'Loading historical market data and initializing backtesting engine...',
      },
      description: 'Backtesting initialization',
      created_at: new Date(Date.now() - 3500000).toISOString(),
    })

    // Add backtesting code
    outputs.push({
      id: `${phaseId}_output_2`,
      output_type: OutputType.CODE_BLOCK,
      data: {
        language: 'python',
        code: `import pandas as pd
import numpy as np
from datetime import datetime

# Load historical price data
prices_df = pd.read_csv('data/historical_prices.csv', parse_dates=['date'])
print(f"Data range: {prices_df['date'].min()} to {prices_df['date'].max()}")
print(f"Total trading days: {len(prices_df)}")

# Initialize portfolio
initial_capital = 1000000
portfolio_value = initial_capital
positions = {}

# Calculate returns
returns = prices_df['close'].pct_change()
sharpe_ratio = np.sqrt(252) * returns.mean() / returns.std()
max_drawdown = (prices_df['close'] / prices_df['close'].cummax() - 1).min()

print(f"Sharpe Ratio: {sharpe_ratio:.2f}")
print(f"Maximum Drawdown: {max_drawdown:.2%}")`,
        correction_attempt: null,
      },
      description: 'Portfolio performance calculation',
      created_at: new Date(Date.now() - 3400000).toISOString(),
    })
  } else {
    // Add data quality analysis
    outputs.push({
      id: `${phaseId}_output_1`,
      output_type: OutputType.TEXT_MESSAGE,
      data: { message: 'Initiating comprehensive data quality assessment...' },
      description: 'Quality check initialization',
      created_at: new Date(Date.now() - 3500000).toISOString(),
    })

    // Add data quality code
    outputs.push({
      id: `${phaseId}_output_2`,
      output_type: OutputType.CODE_BLOCK,
      data: {
        language: 'python',
        code: `import pandas as pd
from dataqualitylib import DataQualityChecker

# Load dataset for quality assessment
df = pd.read_csv('data/financial_transactions.csv')
dq = DataQualityChecker(df)

# Check for missing values
missing_report = dq.check_missing_values()
print(f"Missing values: {missing_report['total_missing']}")
print(f"Completeness score: {missing_report['completeness_score']:.2%}")

# Check data types and format consistency
type_report = dq.check_data_types()
print(f"Type inconsistencies: {type_report['inconsistencies']}")

# Validate business rules
rules_report = dq.validate_business_rules()
print(f"Rule violations: {rules_report['violations']}")`,
        correction_attempt: null,
      },
      description: 'Data quality validation',
      created_at: new Date(Date.now() - 3400000).toISOString(),
    })
  }

  // Add plot image
  const plotData =
    type === 'backtesting'
      ? {
          path: '/api/files/plots/portfolio_performance.png',
          title: 'Portfolio Performance Chart',
          description: 'Cumulative returns vs benchmark over backtest period',
        }
      : {
          path: '/api/files/plots/data_quality_heatmap.png',
          title: 'Data Quality Heatmap',
          description: 'Quality metrics across different data dimensions',
        }

  outputs.push({
    id: `${phaseId}_output_3`,
    output_type: OutputType.PLOT_IMAGE,
    data: plotData,
    description:
      type === 'backtesting'
        ? 'Performance visualization'
        : 'Quality visualization',
    file_path: `/workspace/${phaseId}/plots/${plotData.path.split('/').pop()}`,
    created_at: new Date(Date.now() - 3300000).toISOString(),
  })

  // Add markdown output
  const markdownContent =
    type === 'backtesting'
      ? `## Backtesting Results Summary

### Performance Metrics
- **Total Return**: +18.5%
- **Annualized Return**: 22.3%
- **Sharpe Ratio**: 1.85
- **Maximum Drawdown**: -12.4%
- **Win Rate**: 58.2%

### Risk Analysis
1. **Value at Risk (95%)**: -2.3%
2. **Expected Shortfall**: -3.1%
3. **Beta**: 0.87
4. **Alpha**: 0.04

### Strategy Performance
- Best performing month: +8.2% (March 2024)
- Worst performing month: -4.5% (January 2024)
- Average monthly return: 1.54%`
      : `## Data Quality Report

### Overall Quality Score: 87.3%

### Dimension Analysis
1. **Completeness**: 94.2%
   - Missing values: 5.8% (primarily in optional fields)
   - Required fields: 99.8% complete

2. **Accuracy**: 89.1%
   - Outliers detected: 342 records (0.34%)
   - Format violations: 156 records

3. **Consistency**: 91.7%
   - Cross-field validation: 95.2% pass rate
   - Referential integrity: 98.1%

4. **Timeliness**: 74.8%
   - Average data age: 2.3 days
   - Real-time feed latency: 450ms avg`

  outputs.push({
    id: `${phaseId}_output_4`,
    output_type: OutputType.MARKDOWN_TEXT,
    data: markdownContent,
    description:
      type === 'backtesting'
        ? 'Backtesting summary'
        : 'Quality assessment summary',
    created_at: new Date(Date.now() - 3200000).toISOString(),
  })

  // Add file reference
  outputs.push({
    id: `${phaseId}_output_5`,
    output_type: OutputType.FILE_REFERENCE,
    data: {
      path:
        type === 'backtesting'
          ? '/api/files/session_123/backtest_report.pdf'
          : '/api/files/session_123/data_quality_report.pdf',
      fileType: 'pdf',
      description:
        type === 'backtesting'
          ? 'Complete backtesting report with trade logs'
          : 'Detailed data quality assessment report',
    },
    description:
      type === 'backtesting' ? 'Full backtest report' : 'Quality report',
    file_path: `/workspace/session_123/outputs/${type}_report.pdf`,
    created_at: new Date(Date.now() - 3100000).toISOString(),
  })

  return outputs
}

const generateMockPhases = (
  sessionId: string,
  type: 'backtesting' | 'quality' = 'backtesting',
): ReasoningPhase[] => {
  if (type === 'backtesting') {
    return [
      {
        phase_id: `${sessionId}_phase_1`,
        title: 'Data Collection and Preprocessing',
        goal: 'Load historical market data and prepare for backtesting',
        status: PhaseStatus.COMPLETED,
        started_at: new Date(Date.now() - 3600000).toISOString(),
        ended_at: new Date(Date.now() - 3500000).toISOString(),
        conclusion: 'Successfully loaded 5 years of historical data',
        outputs: generateMockOutputs(`${sessionId}_phase_1`, 'backtesting'),
        reasoning_steps: [
          '🔧 Connecting to market data provider',
          'ℹ️ Loading OHLCV data for selected assets',
          '✅ Data validation and cleaning completed',
        ],
      },
      {
        phase_id: `${sessionId}_phase_2`,
        title: 'Strategy Implementation',
        goal: 'Implement trading logic and signal generation',
        status: PhaseStatus.COMPLETED,
        started_at: new Date(Date.now() - 3500000).toISOString(),
        ended_at: new Date(Date.now() - 3000000).toISOString(),
        conclusion: 'Trading signals generated successfully',
        outputs: generateMockOutputs(`${sessionId}_phase_2`, 'backtesting'),
        reasoning_steps: [
          '📊 Calculating technical indicators',
          '🔍 Implementing entry/exit logic',
          '📈 Generating position signals',
          '✅ Strategy logic validated',
        ],
      },
      {
        phase_id: `${sessionId}_phase_3`,
        title: 'Performance Analysis',
        goal: 'Calculate returns and risk metrics',
        status: PhaseStatus.COMPLETED,
        started_at: new Date(Date.now() - 3000000).toISOString(),
        ended_at: new Date(Date.now() - 2000000).toISOString(),
        conclusion: 'Achieved 1.85 Sharpe ratio with 18.5% annual return',
        outputs: generateMockOutputs(`${sessionId}_phase_3`, 'backtesting'),
        reasoning_steps: [
          '🔧 Computing portfolio returns',
          '📊 Calculating risk metrics',
          '📈 Benchmarking against S&P 500',
          '✅ Performance report generated',
        ],
      },
    ]
  } else {
    return [
      {
        phase_id: `${sessionId}_phase_1`,
        title: 'Data Source Connection',
        goal: 'Connect to data feeds and establish baseline',
        status: PhaseStatus.COMPLETED,
        started_at: new Date(Date.now() - 3600000).toISOString(),
        ended_at: new Date(Date.now() - 3500000).toISOString(),
        conclusion: 'Connected to all required data sources',
        outputs: generateMockOutputs(`${sessionId}_phase_1`, 'quality'),
        reasoning_steps: [
          '🔧 Establishing feed connections',
          'ℹ️ Verifying data access permissions',
          '✅ All connections active',
        ],
      },
      {
        phase_id: `${sessionId}_phase_2`,
        title: 'Quality Metrics Computation',
        goal: 'Assess data quality across multiple dimensions',
        status: PhaseStatus.COMPLETED,
        started_at: new Date(Date.now() - 3500000).toISOString(),
        ended_at: new Date(Date.now() - 3000000).toISOString(),
        conclusion: 'Quality assessment completed - Score: 87.3%',
        outputs: generateMockOutputs(`${sessionId}_phase_2`, 'quality'),
        reasoning_steps: [
          '📊 Analyzing completeness metrics',
          '🔍 Checking data accuracy',
          '📈 Validating consistency rules',
          '✅ Quality scores calculated',
        ],
      },
      {
        phase_id: `${sessionId}_phase_3`,
        title: 'Issue Identification and Reporting',
        goal: 'Identify quality issues and generate recommendations',
        status: PhaseStatus.COMPLETED,
        started_at: new Date(Date.now() - 3000000).toISOString(),
        ended_at: new Date(Date.now() - 2000000).toISOString(),
        conclusion: 'Identified 12 critical issues requiring attention',
        outputs: generateMockOutputs(`${sessionId}_phase_3`, 'quality'),
        reasoning_steps: [
          '🔧 Analyzing anomalies',
          '📊 Prioritizing issues by impact',
          '📝 Generating remediation plan',
          '✅ Report finalized',
        ],
      },
    ]
  }
}

export const mockReasoningTraces: ReasoningTrace[] = [
  {
    session_id: 'session_001',
    conversation_id: 'conv_001',
    title: 'S&P 500 Momentum Strategy Backtest - 2024',
    started_at: new Date(Date.now() - 7200000).toISOString(),
    ended_at: new Date(Date.now() - 3600000).toISOString(),
    duration: 3600,
    status: 'completed',
    working_directory: '/workspace/session_001',
    phases: generateMockPhases('session_001', 'backtesting'),
    files: generateMockFiles('session_001', 5),
    metadata: {
      strategy_type: 'momentum',
      backtest_period: '2020-2024',
      initial_capital: '$1,000,000',
      asset_universe: 'S&P 500',
    },
    datasources: [
      {
        datasource_id: 'ds_001',
        name: 'KambaData Demo Snowflake',
        type: 'snowflake',
        usage_count: 1250,
      },
      {
        datasource_id: 'ds_002',
        name: 'EDGAR API',
        type: 'api',
        usage_count: 450,
      },
      {
        datasource_id: 'ds_003',
        name: 'Brain Sentiment Analysis Product',
        type: 'product_catalog',
        usage_count: 320,
      },
    ],
    summary: {
      total_phases: 3,
      completed_phases: 3,
      error_phases: 0,
      total_files: 5,
      total_outputs: 15,
    },
  },
  {
    session_id: 'session_002',
    conversation_id: 'conv_002',
    title: 'NYSE Market Data Quality Assessment - Q3 2024',
    started_at: new Date(Date.now() - 14400000).toISOString(),
    ended_at: new Date(Date.now() - 10800000).toISOString(),
    duration: 3600,
    status: 'completed',
    working_directory: '/workspace/session_002',
    phases: generateMockPhases('session_002', 'quality'),
    files: generateMockFiles('session_002', 8),
    metadata: {
      data_source: 'NYSE TAQ',
      assessment_type: 'comprehensive',
      total_symbols: 2800,
      date_range: '2024-07-01 to 2024-09-30',
    },
    datasources: [
      {
        datasource_id: 'ds_001',
        name: 'KambaData Demo Snowflake',
        type: 'snowflake',
        usage_count: 8500,
      },
      {
        datasource_id: 'ds_002',
        name: 'EDGAR API',
        type: 'api',
        usage_count: 2300,
      },
    ],
    summary: {
      total_phases: 4,
      completed_phases: 4,
      error_phases: 0,
      total_files: 8,
      total_outputs: 20,
    },
  },
  {
    session_id: 'session_003',
    conversation_id: 'conv_003',
    title: 'Crypto Portfolio Optimization Backtest',
    started_at: new Date(Date.now() - 1800000).toISOString(),
    status: 'running',
    working_directory: '/workspace/session_003',
    phases: [
      {
        phase_id: 'session_003_phase_1',
        title: 'Historical Data Collection',
        goal: 'Gather crypto price data from multiple exchanges',
        status: PhaseStatus.COMPLETED,
        started_at: new Date(Date.now() - 1800000).toISOString(),
        ended_at: new Date(Date.now() - 1200000).toISOString(),
        conclusion: 'Successfully collected data for BTC, ETH, SOL, MATIC',
        outputs: generateMockOutputs('session_003_phase_1', 'backtesting'),
        reasoning_steps: [
          '✅ Connected to Binance, Coinbase APIs',
          '✅ Data validation completed',
        ],
      },
      {
        phase_id: 'session_003_phase_2',
        title: 'Portfolio Optimization',
        goal: 'Calculate optimal weights using Markowitz framework',
        status: PhaseStatus.RUNNING,
        started_at: new Date(Date.now() - 1200000).toISOString(),
        outputs: [],
        reasoning_steps: [
          '🔧 Computing covariance matrix...',
          '🔧 Running mean-variance optimization...',
        ],
      },
    ],
    files: generateMockFiles('session_003', 3),
    metadata: {
      optimization_method: 'Mean-Variance',
      rebalance_frequency: 'Monthly',
      assets: ['BTC', 'ETH', 'SOL', 'MATIC'],
    },
    summary: {
      total_phases: 2,
      completed_phases: 1,
      error_phases: 0,
      total_files: 3,
      total_outputs: 5,
    },
  },
  {
    session_id: 'session_004',
    conversation_id: 'conv_004',
    title: 'Bloomberg Feed Data Integrity Check - Failed',
    started_at: new Date(Date.now() - 28800000).toISOString(),
    ended_at: new Date(Date.now() - 25200000).toISOString(),
    duration: 3600,
    status: 'error',
    working_directory: '/workspace/session_004',
    phases: [
      {
        phase_id: 'session_004_phase_1',
        title: 'Feed Connection',
        status: PhaseStatus.ERROR,
        started_at: new Date(Date.now() - 28800000).toISOString(),
        ended_at: new Date(Date.now() - 28500000).toISOString(),
        conclusion: 'Failed to establish connection to Bloomberg Terminal',
        outputs: [
          {
            id: 'session_004_phase_1_error',
            output_type: OutputType.ERROR_MESSAGE,
            data: {
              error: 'ConnectionError: Bloomberg API authentication failed',
              traceback:
                'Traceback (most recent call last):\n  File "bloomberg_connector.py", line 127...',
            },
            description: 'API connection error',
            created_at: new Date(Date.now() - 28500000).toISOString(),
          },
        ],
        reasoning_steps: [
          '🔧 Attempting to connect to Bloomberg Terminal',
          '❌ Error: Authentication credentials invalid or expired',
        ],
      },
    ],
    files: [],
    metadata: {
      data_source: 'Bloomberg Terminal',
      connection_type: 'API',
      error_type: 'AuthenticationError',
    },
    summary: {
      total_phases: 1,
      completed_phases: 0,
      error_phases: 1,
      total_files: 0,
      total_outputs: 1,
    },
  },
]

// Generate more mock data for pagination testing with financial themes
const financialTasks = [
  'NASDAQ 100 Mean Reversion Strategy Backtest',
  'EUR/USD FX Data Quality Validation',
  'Treasury Bond Yield Curve Analysis',
  'Options Market Maker Strategy Simulation',
  'Real-time Feed Latency Assessment - CME',
  'Multi-Asset Risk Parity Portfolio Backtest',
  'Corporate Bond Credit Spread Analysis',
  'High-Frequency Trading Data Integrity Check',
  'VIX Futures Trading Strategy Evaluation',
  'Equity Market Microstructure Analysis',
  'Commodities Futures Roll Strategy Test',
  'Dark Pool Liquidity Assessment',
  'ETF Arbitrage Opportunity Scanner',
  'Forex Carry Trade Performance Analysis',
  'Municipal Bond Portfolio Optimization',
  'Derivatives Pricing Model Validation',
]

for (let i = 5; i <= 20; i++) {
  const status = i % 3 === 0 ? 'error' : i % 5 === 0 ? 'running' : 'completed'
  const isBacktest = i % 2 === 0
  const taskTitle = financialTasks[i - 5] || `Financial Analysis Task ${i}`

  const trace: ReasoningTrace = {
    session_id: `session_${String(i).padStart(3, '0')}`,
    conversation_id: `conv_${String(i).padStart(3, '0')}`,
    title: `${taskTitle}${
      status === 'error'
        ? ' - Failed'
        : status === 'running'
        ? ' - In Progress'
        : ''
    }`,
    started_at: new Date(Date.now() - i * 3600000).toISOString(),
    ended_at:
      status !== 'running'
        ? new Date(Date.now() - (i - 1) * 3600000).toISOString()
        : undefined,
    duration: status !== 'running' ? 3600 : undefined,
    status,
    working_directory: `/workspace/session_${String(i).padStart(3, '0')}`,
    phases:
      status === 'error'
        ? [
            {
              phase_id: `session_${String(i).padStart(3, '0')}_phase_1`,
              title: 'Failed Phase',
              status: PhaseStatus.ERROR,
              started_at: new Date(Date.now() - i * 3600000).toISOString(),
              ended_at: new Date(
                Date.now() - (i - 0.5) * 3600000,
              ).toISOString(),
              conclusion: 'Process failed',
              outputs: [],
              reasoning_steps: ['❌ Error occurred'],
            },
          ]
        : generateMockPhases(
            `session_${String(i).padStart(3, '0')}`,
            isBacktest ? 'backtesting' : 'quality',
          ),
    files:
      status === 'completed'
        ? generateMockFiles(
            `session_${String(i).padStart(3, '0')}`,
            Math.floor(Math.random() * 10) + 1,
          )
        : [],
    metadata: isBacktest
      ? {
          strategy_type: [
            'momentum',
            'mean_reversion',
            'arbitrage',
            'market_making',
          ][Math.floor(Math.random() * 4)],
          backtest_period: '2020-2024',
          initial_capital: `$${Math.floor(Math.random() * 9 + 1)}00,000`,
          asset_class: ['equities', 'futures', 'options', 'forex'][
            Math.floor(Math.random() * 4)
          ],
        }
      : {
          data_source: ['Reuters', 'Bloomberg', 'ICE', 'CME', 'NYSE'][
            Math.floor(Math.random() * 5)
          ],
          quality_score: `${Math.floor(Math.random() * 15 + 85)}%`,
          total_records: Math.floor(Math.random() * 1000000) + 100000,
          assessment_type: [
            'completeness',
            'accuracy',
            'timeliness',
            'consistency',
          ][Math.floor(Math.random() * 4)],
        },
    summary: {
      total_phases: status === 'error' ? 1 : 3,
      completed_phases: status === 'error' ? 0 : status === 'running' ? 1 : 3,
      error_phases: status === 'error' ? 1 : 0,
      total_files:
        status === 'completed' ? Math.floor(Math.random() * 10) + 1 : 0,
      total_outputs: status === 'completed' ? 15 : status === 'running' ? 5 : 1,
    },
  }
  mockReasoningTraces.push(trace)
}
